import { useState } from "react";
import { createNotificationTemplate } from "../../../../Actions/AdminActions";
import { formatApiError } from "../../../../Actions/Utils";

const EMPTY_FORM = {
  name: "",
  mrTitle: "",
  mrBody: "",
  orTitle: "",
  orBody: "",
};

const NAME_PATTERN = /^[a-z0-9_]+$/;

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function inputClass(hasError) {
  return `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
    hasError ? "border-red-400 bg-red-50" : "border-gray-300"
  }`;
}

export default function CreateTemplateForm({ onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  function validate() {
    const errors = {};
    const name = form.name.trim();

    if (!name) {
      errors.name = "Template name is required.";
    } else if (!NAME_PATTERN.test(name)) {
      errors.name = "Use lowercase letters, numbers, and underscores only. No spaces.";
    }

    if (!form.mrTitle.trim()) errors.mrTitle = "Title is required.";
    if (!form.mrBody.trim()) errors.mrBody = "Body is required.";
    if (!form.orTitle.trim()) errors.orTitle = "Title is required.";
    if (!form.orBody.trim()) errors.orBody = "Body is required.";

    return errors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (creating) return;

    const errors = validate();
    setFieldErrors(errors);
    setError("");
    setSuccess("");
    if (Object.keys(errors).length > 0) return;

    setCreating(true);
    const name = form.name.trim();

    try {
      const res = await createNotificationTemplate({
        name,
        translations: {
          mr: { title: form.mrTitle.trim(), body: form.mrBody.trim() },
          or: { title: form.orTitle.trim(), body: form.orBody.trim() },
        },
      });

      if (res?.error) {
        setError(
          "Failed to create template. Please check your connection and try again."
        );
        return;
      }

      if (res?.status === 201) {
        const createdName = res.data?.name || name;
        setSuccess(`Template '${createdName}' created successfully!`);
        setForm(EMPTY_FORM);
        setFieldErrors({});
        if (onCreated) onCreated();
        return;
      }

      if (res?.status === 409) {
        setFieldErrors({
          name: "A template with this name already exists. Choose a different name.",
        });
        return;
      }

      setError(
        formatApiError(res?.data?.detail) ||
          res?.data?.message ||
          "Failed to create template. Please try again."
      );
    } finally {
      setCreating(false);
    }
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900">Create Template</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Define Marathi and Odia copy for a reusable notification template.
        </p>
      </div>

      {error && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <Field label="Template Name" required error={fieldErrors.name}>
            <input
              type="text"
              placeholder="e.g. anc_reminder_june"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className={inputClass(fieldErrors.name)}
            />
          </Field>
          <p className="text-xs text-gray-500 mt-1 mb-0">
            This name is used when sending a campaign. Cannot be changed later.
          </p>

          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
              Marathi (Maharashtra)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title (Marathi)" required error={fieldErrors.mrTitle}>
                <input
                  type="text"
                  placeholder="e.g. एएनसी स्मरणपत्र"
                  value={form.mrTitle}
                  onChange={(e) => update("mrTitle", e.target.value)}
                  className={inputClass(fieldErrors.mrTitle)}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Body (Marathi)" required error={fieldErrors.mrBody}>
                  <textarea
                    rows={3}
                    placeholder="e.g. तुमची भेट उद्या आहे..."
                    value={form.mrBody}
                    onChange={(e) => update("mrBody", e.target.value)}
                    className={inputClass(fieldErrors.mrBody)}
                  />
                </Field>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
              Odia (Odisha)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title (Odia)" required error={fieldErrors.orTitle}>
                <input
                  type="text"
                  placeholder="e.g. ANC ଅନୁସ୍ମାରକ"
                  value={form.orTitle}
                  onChange={(e) => update("orTitle", e.target.value)}
                  className={inputClass(fieldErrors.orTitle)}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Body (Odia)" required error={fieldErrors.orBody}>
                  <textarea
                    rows={3}
                    placeholder="e.g. ଆପଣଙ୍କ ପରିଦର୍ଶନ କାଲି ଅଛି..."
                    value={form.orBody}
                    onChange={(e) => update("orBody", e.target.value)}
                    className={inputClass(fieldErrors.orBody)}
                  />
                </Field>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating…
                </>
              ) : (
                "Create Template"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
